import { scrollToElement } from "../lib/scroll";

function findTocTarget(container: Element, targetId: string): Element | null {
  const root = container.getRootNode();
  if ("getElementById" in root) {
    const target = (root as Document | ShadowRoot).getElementById(targetId);
    return target && container.contains(target) ? target : null;
  }

  // Detached containers used before insertion do not have an ID lookup API.
  for (const element of container.querySelectorAll("[id]")) {
    if (element.id === targetId) return element;
  }

  return null;
}

function scrollToTocTarget(
  container: Element,
  scrollContainer: HTMLElement,
  targetId: string,
): void {
  const targetElement = findTocTarget(container, targetId);
  if (!targetElement) return;
  scrollToElement(scrollContainer, targetElement);
}

export function buildTocDom(
  container: Element,
  scrollContainer: HTMLElement,
): void {
  // Select all headers and add IDs to those without them
  const allHeaders = container.querySelectorAll("h2, h3, h4, h5, h6");

  // WEIRDLY my buligarian dump didnt have any ids
  allHeaders.forEach((header, index) => {
    if (!header.id) {
      const text = header.textContent?.trim() || `section-${index}`;
      header.id =
        text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || `section-${index}`;
    }
  });

  const headers = container.querySelectorAll(
    "h2[id], h3[id], h4[id], h5[id], h6[id]",
  );
  if (headers.length <= 3) return;

  const toc = document.createElement("fieldset");
  toc.id = "toc";

  const articleBody = container.querySelector("#article_body");
  if (!articleBody) return;

  if (articleBody.firstElementChild) {
    articleBody.firstElementChild.insertAdjacentElement("afterend", toc);
  } else {
    articleBody.insertAdjacentElement("afterbegin", toc);
  }

  const legend = document.createElement("legend");
  legend.textContent = "Table Of Content";
  toc.appendChild(legend);

  const rootUl = document.createElement("ul");
  toc.appendChild(rootUl);

  const stack: { level: number; ul: HTMLUListElement }[] = [
    { level: 1, ul: rootUl },
  ];

  headers.forEach((header) => {
    const level = parseInt(header.tagName[1]);
    const li = document.createElement("li");
    li.classList.add(`level-${level}`);

    while (stack.length > 1 && level <= stack[stack.length - 1].level) {
      stack.pop();
    }

    if (level === 2) {
      li.classList.add("collapsible-item");
      const icon = document.createElement("span");
      icon.classList.add("toggle-icon");
      icon.textContent = "▶";
      li.appendChild(icon);

      const text = document.createElement("span");
      text.textContent = header.textContent || "";
      text.style.cursor = "pointer";
      text.dataset.targetId = header.id;
      li.appendChild(text);

      const newUl = document.createElement("ul");
      newUl.style.display = "none";
      li.appendChild(newUl);
      stack.push({ level, ul: newUl });
      rootUl.appendChild(li);
    } else {
      li.textContent = header.textContent || "";
      li.style.cursor = "pointer";
      li.dataset.targetId = header.id;
      stack[stack.length - 1].ul.appendChild(li);
    }
  });

  const allH2Lis = toc.querySelectorAll("li > .toggle-icon");
  allH2Lis.forEach((icon) => {
    const li = icon.parentElement;
    if (!li) return;
    const nestedUl = li.querySelector("ul");
    if (!nestedUl || nestedUl.children.length === 0) {
      (icon as HTMLElement).style.display = "none";
      li.classList.remove("collapsible-item");
    }
  });

  toc.addEventListener("click", (e) => {
    const target = e.target as Element;
    const li = target.closest("li");

    if (!li) return;

    if (target.classList.contains("toggle-icon")) {
      const nestedUl = li.querySelector("ul");
      if (nestedUl) {
        const isCollapsed = nestedUl.style.display === "none";
        nestedUl.style.display = isCollapsed ? "block" : "none";
        const icon = li.querySelector(".toggle-icon");
        if (icon) icon.textContent = isCollapsed ? "▼" : "▶";
      }
    } else if (target.tagName === "SPAN" && li.querySelector("ul")) {
      const nestedUl = li.querySelector("ul");
      if (nestedUl) {
        const isCollapsed = nestedUl.style.display === "none";
        nestedUl.style.display = isCollapsed ? "block" : "none";
        const icon = li.querySelector(".toggle-icon");
        if (icon) icon.textContent = isCollapsed ? "▼" : "▶";
      }
      const targetId = (target as HTMLElement).dataset.targetId;
      if (targetId) {
        scrollToTocTarget(container, scrollContainer, targetId);
      }
    } else if (li.dataset.targetId) {
      scrollToTocTarget(container, scrollContainer, li.dataset.targetId);
    }
  });
}

if (import.meta.vitest) {
  const { describe, it, expect, vi } = import.meta.vitest;

  describe("buildTocDom", () => {
    it("scrolls to headings whose IDs contain apostrophes", () => {
      const container = document.createElement("div");
      const scrollContainer = document.createElement("div");
      scrollContainer.appendChild(container);
      container.innerHTML = `
        <div id="article_body">
          <p>Introduction</p>
          <h2 id="Différents_niveaux_d'organisation">Organization</h2>
          <h2 id="section-2">Section 2</h2>
          <h2 id="section-3">Section 3</h2>
          <h2 id="section-4">Section 4</h2>
        </div>
      `;
      vi.stubGlobal(
        "matchMedia",
        vi.fn(() => ({ matches: false })),
      );
      const scrollToSpy = vi.fn();
      scrollContainer.scrollTo = scrollToSpy;

      buildTocDom(container, scrollContainer);
      const tocItem = container.querySelector("#toc [data-target-id]");

      expect(tocItem).not.toBeNull();
      expect(() =>
        tocItem?.dispatchEvent(new MouseEvent("click", { bubbles: true })),
      ).not.toThrow();
      expect(scrollToSpy).toHaveBeenCalledWith({
        top: 0,
        behavior: "instant",
      });

      vi.unstubAllGlobals();
    });

    it("does not append the same list item twice", () => {
      const container = document.createElement("div");
      const scrollContainer = document.createElement("div");
      scrollContainer.appendChild(container);
      container.innerHTML = `
        <div id="article_body">
          <p>Introduction</p>
          <h2 id="section-1">Section 1</h2>
          <h3 id="section-1-1">Sub 1.1</h3>
          <h4 id="section-1-1-1">Sub 1.1.1</h4>
          <h2 id="section-2">Section 2</h2>
        </div>
      `;
      vi.stubGlobal(
        "matchMedia",
        vi.fn(() => ({ matches: false })),
      );

      buildTocDom(container, scrollContainer);

      const subLi = container.querySelector(
        '#toc [data-target-id="section-1-1"]',
      );
      expect(subLi).not.toBeNull();
      expect(
        subLi?.parentElement?.querySelectorAll(
          '[data-target-id="section-1-1"]',
        ),
      ).toHaveLength(1);
      expect(subLi?.parentElement?.children).toHaveLength(2);

      vi.unstubAllGlobals();
    });
  });
}
